// @ts-check
// Linha única do atendimento (contrato 10 de atendimento, seção 24.2): mensagens da conversa, notas internas (fundo
// âmbar, "Nota interna") e eventos (linha fina: "Transferido de Secretaria para Financeiro: departamento errado") em
// ordem. As bolhas do WhatsApp são a mesma `MessageBubble` da conversa da 2.0.0 (status, selo de bot e de aviso
// automático, anexos, figurinha, resposta citada e reações); no modo "Dividir" cada mensagem ganha a caixa de seleção.
import { html } from 'htm/preact';
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { buildServiceTimeline, canEditServiceNote, splitNoteBody } from '../../lib/service-inbox-rules.js';
import { formatDaySeparator, formatFullDateTime, formatTime, getDayKey } from '../../lib/conversation-format.js';
import { MessageAttachmentView } from '../conversations/message-attachments.js';
import { MessageBubble, collectReactions } from '../conversations/whatsapp-message-list.js';
import { Checkbox } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { LightIconButton, Button } from '../../ui/button.js';
import { ticketValueLabel } from './service-display.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/service-desk.js').ServiceNote} ServiceNote
 * @typedef {import('../../data/service-desk.js').ServiceTicketEvent} ServiceTicketEvent
 */

/** Ícone de cada evento na linha. */
const EVENT_ICONS = Object.freeze(/** @type {Record<string, string>} */ ({
  CREATED: 'IconPlus', STATUS_CHANGED: 'IconCircleDot', ASSIGNED: 'IconUserCheck', TRANSFERRED: 'IconTransferIn', CONSULTATION_OPENED: 'IconMessageQuestion',
  CONSULTATION_ANSWERED: 'IconMessageQuestion', CONSULTATION_CANCELED: 'IconX', MERGED: 'IconArrowMerge', MERGED_INTO: 'IconArrowMerge', SPLIT_FROM: 'IconLayoutRows',
  SPLIT_TO: 'IconLayoutRows', REOPENED: 'IconRefresh', CSAT_SENT: 'IconStar', CSAT_ANSWERED: 'IconStar', SLA_WARNED: 'IconClock', SLA_BREACHED: 'IconAlertTriangle',
  IDENTITY_CHANGED: 'IconId', PROTECTION_ESCALATED: 'IconShieldLock', COUNCIL_NOTIFIED: 'IconShieldCheck', RETENTION_OUTCOME: 'IconHeartHandshake', PURPOSE_CHANGED: 'IconSwitchHorizontal',
}));

/**
 * Variáveis do texto de um evento: nomes, valores com rótulo e o motivo da transferência.
 * @param {ServiceTicketEvent} event
 */
export function eventVariables(event) {
  const details = event.details ?? {};
  return {
    actor: event.actorName ?? t(`serviceDesk.events.actors.${event.actorSource ?? 'SYSTEM'}`),
    from: details.from ? ticketValueLabel('status', details.from) : '',
    to: details.to ? ticketValueLabel('status', details.to) : '',
    fromTeam: details.fromTeamName ?? '',
    toTeam: details.toTeamName ?? '',
    reason: details.reason ? t(`serviceDesk.transferReasons.${details.reason}`) : '',
    assignee: details.assigneeName ?? '',
    target: details.targetName ?? '',
    answeredBy: details.answeredByName ?? '',
    protocol: details.protocol ?? '',
    rating: details.rating ? ticketValueLabel('csatRating', details.rating) : '',
    person: details.personName ?? '',
    level: details.level ? ticketValueLabel('identityLevel', details.level) : '',
    category: details.category ? ticketValueLabel('category', details.category) : '',
    council: details.councilName ?? '',
    count: String(details.messageCount ?? ''),
    outcome: details.outcome ? ticketValueLabel('retentionOutcome', details.outcome) : '',
  };
}

/** @param {{ event: ServiceTicketEvent }} props */
function EventLine({ event }) {
  const key = event.kind === 'ASSIGNED' && event.details?.byDistribution ? 'serviceDesk.events.ASSIGNED_DISTRIBUTION' : `serviceDesk.events.${event.kind}`;
  return html`<div class="service-event-line" data-kind=${event.kind} data-testid="service-event">
    <${Icon} name=${EVENT_ICONS[event.kind] ?? 'IconCircleDot'} size=${12} />
    <span class="service-event-text">${t(key, eventVariables(event))}</span>
    <time datetime=${event.occurredAt} title=${formatFullDateTime(event.occurredAt)}>${formatTime(event.occurredAt)}</time>
  </div>`;
}

/**
 * @param {{ note: ServiceNote, viewerWorkspaceMemberId: string | null, nameOf: (id: string) => string | null,
 *   onEdit: (note: ServiceNote, body: string) => Promise<boolean>, onDelete: (note: ServiceNote) => void }} props
 */
function NoteCard({ note, viewerWorkspaceMemberId, nameOf, onEdit, onDelete }) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(note.body);
  const kindKey = `serviceDesk.notes.kinds.${note.kind}`;
  if (note.deletedAt) {
    return html`<div class="service-note" data-deleted="true" data-testid="service-note"><${Icon} name="IconNotes" size=${14} /><em>${t('serviceDesk.notes.deleted')}</em></div>`;
  }
  const canEdit = canEditServiceNote(note, viewerWorkspaceMemberId);
  const author = note.authorName ?? (note.authorSource === 'BOT' ? t('serviceDesk.events.actors.BOT') : t('serviceDesk.events.actors.SYSTEM'));
  return html`<div class="service-note" data-kind=${note.kind} data-testid="service-note">
    <div class="service-note-header">
      <${Icon} name=${note.kind === 'CONSULTATION_ANSWER' ? 'IconMessageQuestion' : note.kind === 'BOT_SUMMARY' ? 'IconRobot' : 'IconNotes'} size=${14} />
      <span class="service-note-kind">${t(kindKey)}</span>
      <span class="service-note-author">${note.kind === 'CONSULTATION_ANSWER' ? t('serviceDesk.notes.answerFrom', { name: author }) : author}</span>
      <time datetime=${note.createdAt} title=${formatFullDateTime(note.createdAt)}>${formatTime(note.createdAt)}</time>
      ${note.editedAt ? html`<span class="service-note-edited">${t('serviceDesk.notes.edited')}</span>` : null}
      ${canEdit && !isEditing
        ? html`<span class="service-note-actions">
            <${LightIconButton} icon="IconPencil" ariaLabel=${t('serviceDesk.notes.edit')} onClick=${() => {
              setDraft(note.body);
              setIsEditing(true);
            }} />
            <${LightIconButton} icon="IconTrash" ariaLabel=${t('serviceDesk.notes.delete')} onClick=${() => onDelete(note)} />
          </span>`
        : null}
    </div>
    ${isEditing
      ? html`<div class="service-note-edit">
          <textarea class="text-input service-textarea" rows="3" value=${draft} aria-label=${t('serviceDesk.notes.edit')}
            onInput=${(/** @type {Event} */ event) => setDraft(/** @type {HTMLTextAreaElement} */ (event.currentTarget).value)}></textarea>
          <div class="service-inline-actions">
            <${Button} title=${t('common.cancel')} variant="secondary" size="small" onClick=${() => setIsEditing(false)} />
            <${Button} title=${t('serviceDesk.notes.save')} accent="blue" size="small" disabled=${!draft.trim()} onClick=${async () => {
              if (await onEdit(note, draft.trim())) setIsEditing(false);
            }} />
          </div>
        </div>`
      : html`<div class="service-note-body">${splitNoteBody(note.body, nameOf).map((part, index) => (part.kind === 'mention'
        ? html`<span key=${index} class="service-mention">@${part.name || t('serviceDesk.notes.unknownMember')}</span>`
        : html`<span key=${index}>${part.text}</span>`))}</div>`}
  </div>`;
}

/**
 * @param {{ message: any, isEmail: boolean, contactName: string, isSelecting: boolean, isSelected: boolean, onToggle: () => void,
 *   quoted?: any, reactions?: { emoji: string, direction: string }[] }} props
 */
function MessageEntry({ message, isEmail, contactName, isSelecting, isSelected, onToggle, quoted, reactions }) {
  const isOutgoing = message.direction === 'OUTGOING';
  const attachments = message.attachments ?? [];
  const selector = isSelecting
    ? html`<span class="service-split-checkbox"><${Checkbox} checked=${isSelected} onChange=${onToggle} ariaLabel=${t('serviceDesk.split.selectMessage')} /></span>`
    : null;
  if (isEmail) {
    return html`<div class="service-email-message" data-direction=${message.direction} data-message-id=${message.id} data-selected=${isSelected ? 'true' : undefined}>
      ${selector}
      <div class="service-email-card">
        <div class="service-email-header">
          <${Icon} name="IconMail" size=${14} />
          <span class="service-email-from">${isOutgoing ? message.sentBy?.name ?? message.from?.displayName ?? '' : message.from?.displayName ?? contactName}</span>
          <time datetime=${message.sentAt} title=${formatFullDateTime(message.sentAt)}>${formatTime(message.sentAt)}</time>
        </div>
        ${message.subject ? html`<div class="service-email-subject">${message.subject}</div>` : null}
        ${message.isRestricted
          ? html`<div class="service-email-text message-restricted"><${Icon} name="IconLock" size=${12} /> ${t('conversations.restrictedMessage')}</div>`
          : html`<div class="service-email-text">${message.text ?? ''}</div>`}
        ${attachments.map((attachment) => html`<div key=${attachment.fileId} class="message-attachment"><${MessageAttachmentView} attachment=${attachment} /></div>`)}
      </div>
    </div>`;
  }
  return html`<${MessageBubble} message=${message} quoted=${quoted} contactName=${contactName} showSender=${true} isFirstInGroup=${true} reactions=${reactions}
    leading=${selector} className="service-message-row" isSelected=${isSelected} />`;
}

// distância do fim que ainda conta como "lendo as últimas mensagens"
const PINNED_DISTANCE_PIXELS = 48;

/** Quem rola a linha do tempo: o painel do atendimento ou, fora dele, ela mesma. @param {HTMLElement} element */
const scrollerOf = (element) => /** @type {HTMLElement} */ (element.closest('.service-pane-scroll') ?? element);

/**
 * @param {{ messages: any[], notes: ServiceNote[], events: ServiceTicketEvent[], isEmail: boolean, contactName: string, viewerWorkspaceMemberId: string | null,
 *   nameOf: (id: string) => string | null, onEditNote: (note: ServiceNote, body: string) => Promise<boolean>, onDeleteNote: (note: ServiceNote) => void,
 *   selection?: Set<string> | null, onToggleSelection?: (messageId: string) => void, emptyText?: string }} props
 */
export function ServiceTimeline({ messages, notes, events, isEmail, contactName, viewerWorkspaceMemberId, nameOf, onEditNote, onDeleteNote, selection = null, onToggleSelection, emptyText }) {
  const scrollRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const entries = buildServiceTimeline({ messages, notes, events });
  const messagesById = new Map(messages.map((message) => [message.id, message]));
  const reactionsByMessageId = isEmail ? new Map() : collectReactions(messages);
  const lastKey = entries.length ? `${entries[entries.length - 1].kind}:${entries[entries.length - 1].item.id}` : '';
  // presa ao fim enquanto a pessoa não rolar para cima: o painel muda de altura depois da primeira pintura (barra de
  // presença, Quem é, compositor no celular) e a leitura continua nas mensagens mais recentes
  const isPinnedRef = useRef(true);
  // nova entrada no fim leva a leitura para baixo, como na conversa. Quem rola é o painel do atendimento (a linha do
  // tempo cresce dentro dele com os cabeçalhos); fora dele, a própria linha do tempo
  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (!element) return;
    isPinnedRef.current = true;
    const scroller = scrollerOf(element);
    scroller.scrollTop = scroller.scrollHeight;
  }, [lastKey]);
  useEffect(() => {
    const element = scrollRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return undefined;
    const scroller = scrollerOf(element);
    const onScroll = () => { isPinnedRef.current = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < PINNED_DISTANCE_PIXELS; };
    const observer = new ResizeObserver(() => {
      if (isPinnedRef.current) scroller.scrollTop = scroller.scrollHeight;
    });
    observer.observe(scroller);
    observer.observe(element);
    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      observer.disconnect();
      scroller.removeEventListener('scroll', onScroll);
    };
  }, []);
  /** @type {any[]} */
  const items = [];
  let previousDay = '';
  for (const entry of entries) {
    const dayKey = entry.at ? getDayKey(entry.at) : '';
    if (dayKey && dayKey !== previousDay) {
      items.push(html`<div key=${`day-${dayKey}`} class="message-day-separator"><span>${formatDaySeparator(entry.at)}</span></div>`);
      previousDay = dayKey;
    }
    if (entry.kind === 'event') items.push(html`<${EventLine} key=${`event-${entry.item.id}`} event=${entry.item} />`);
    else if (entry.kind === 'note') {
      items.push(html`<${NoteCard} key=${`note-${entry.item.id}`} note=${entry.item} viewerWorkspaceMemberId=${viewerWorkspaceMemberId} nameOf=${nameOf}
        onEdit=${onEditNote} onDelete=${onDeleteNote} />`);
    } else {
      // reação a uma mensagem que está na linha vira o emoji embaixo dela, como na conversa
      if (!isEmail && entry.item.reaction && entry.item.replyToMessageId && messagesById.has(entry.item.replyToMessageId)) continue;
      items.push(html`<${MessageEntry} key=${`message-${entry.item.id}`} message=${entry.item} isEmail=${isEmail} contactName=${contactName}
        isSelecting=${Boolean(selection)} isSelected=${Boolean(selection?.has(entry.item.id))} onToggle=${() => onToggleSelection?.(entry.item.id)}
        quoted=${entry.item.replyToMessageId ? messagesById.get(entry.item.replyToMessageId) : undefined} reactions=${reactionsByMessageId.get(entry.item.id)} />`);
    }
  }
  return html`<div class="service-timeline whatsapp-messages" ref=${scrollRef} data-testid="service-timeline">
    <div class="whatsapp-messages-inner service-timeline-inner">
      ${items.length ? items : html`<div class="service-timeline-empty">${emptyText ?? t('serviceDesk.timeline.empty')}</div>`}
    </div>
  </div>`;
}
